import { DEFAULT_ARTIFACT_TITLES_BY_KIND } from "connected-components/dashboard/modules/ai-artifact/constants"
import { ARTIFACT_KINDS } from "connected-components/dashboard/modules/ai-artifact/types"
import type { ArtifactKind } from "connected-components/dashboard/modules/ai-artifact/types"
import type { AiTeammateArtifact } from "../hooks/use-pin-artifact"

const MAX_TITLE_LENGTH = 80
const DEFAULT_ARTIFACT_TITLES = new Set(
  Object.values(DEFAULT_ARTIFACT_TITLES_BY_KIND).map(title => title.toLowerCase()),
)

/**
 * Plotly's TS types declare layout.title as an object, but the AI returns
 * it as a plain string in practice. Both shapes are handled here.
 */
interface PlotlyLayout {
  title?: string | { text?: string }
}

interface MessageArtifact {
  id: string
  type?: string
  tableArtifactId?: string
  figure?: {
    layout?: PlotlyLayout
  }
}

export interface AiTeammateMessage {
  role?: string
  text?: string
  artifacts?: MessageArtifact[]
}

interface DerivePinnedArtifactTitleOptions {
  artifact: AiTeammateArtifact
  kind: ArtifactKind
  messages?: AiTeammateMessage[]
}

function normalizeTitleCandidate(value: unknown): string | null {
  if (typeof value !== "string") return null

  const normalized = value.trim().replace(/\s+/g, " ")
  if (!normalized) return null

  return normalized.length <= MAX_TITLE_LENGTH
    ? normalized
    : `${normalized.slice(0, MAX_TITLE_LENGTH - 3).trimEnd()}...`
}

function isGenericAiTitle(value: string): boolean {
  return DEFAULT_ARTIFACT_TITLES.has(value?.toLowerCase())
}

function getLayoutTitle(layout: PlotlyLayout | undefined): string | null {
  const { title } = layout ?? {}
  if (typeof title === "string") return normalizeTitleCandidate(title)
  return normalizeTitleCandidate(title?.text)
}

function getPlotlyFigureTitle(artifact: AiTeammateArtifact): string | null {
  return "figure" in artifact && artifact.figure
    ? getLayoutTitle(artifact.figure.layout)
    : null
}

function findRelatedPlotTableTitle(messages: AiTeammateMessage[], tableArtifactId: string): string | null {
  const relatedPlot = messages
    .flatMap(message => message.artifacts ?? [])
    .find(artifact => artifact.type === "plot" && artifact.tableArtifactId === tableArtifactId)

  if (!relatedPlot) return null

  const title = getLayoutTitle(relatedPlot.figure?.layout)
  return title ? `${title} (Table)` : null
}

export function derivePinnedArtifactTitle({
  artifact,
  kind,
  messages = [],
}: DerivePinnedArtifactTitleOptions): string {
  if (kind === ARTIFACT_KINDS.TABLE) {
    const relatedPlotTableTitle = findRelatedPlotTableTitle(messages, artifact.id)
    if (relatedPlotTableTitle) return relatedPlotTableTitle
  }

  if (kind === ARTIFACT_KINDS.PLOT) {
    const plotlyFigureTitle = getPlotlyFigureTitle(artifact)
    if (plotlyFigureTitle) return plotlyFigureTitle
  }

  const artifactTitle = normalizeTitleCandidate(artifact.title)
  if (artifactTitle && !isGenericAiTitle(artifactTitle)) return artifactTitle

  return DEFAULT_ARTIFACT_TITLES_BY_KIND[kind]
}
